import { NotifyFail } from '@/helpers/core/notifications'
import { onKeyStroke } from '@vueuse/core'
import diff from 'microdiff'
import { ref, toRaw } from 'vue'

/**
 * Additional table-managed row fields
 */
export interface AugmentedRow<T> {
  id?: string | number
  initialValue?: T
  selected?: boolean
  updated?: 'updated' | 'errored' | null
}

/**
 * Augments the data type we're rendering on the rows, with the table-managed row data
 * (`initialValue` buffer to discard edits, `selected` flag, etc)
 */
export type ManagedRow<T> = T & AugmentedRow<T>

interface useInlineEditProps<T> {
  onRowUpdate: (row: T) => Promise<void>
  onUpdateFailedMessageTitle?: string
  onSaveValidation?: (row: T) => Promise<boolean>
}

/**
 * Holds and provides a buffer poiting to the currently-being-edited row. Manages editing
 * state, should-update diff and keyboard listening, with proper cleanup.
 *
 * Use by binding the table's edit icon onClick action to the exported onClickEdit
 * function, and binding the renderers to the exported rowBeingEdited buffer.
 *
 * **Important**: The onRowUpdate function should throw an error if the update fails. Prefer mutateAsync over mutate.
 *
 * If onSaveValidation is provided, it'll be run before saving to determine if the row can be saved.
 *
 * @param onUpdate: The update function to run upon editing (after verifying actual row data update)
 */
export const useRowInlineEdit = <T>({
  onRowUpdate,
  onUpdateFailedMessageTitle,
  onSaveValidation,
}: useInlineEditProps<T>) => {
  const rowBeingEdited = ref<ManagedRow<T> | undefined>(undefined)

  /**
   * Toggle the edit mode for a row
   */
  async function onClickEdit(row: ManagedRow<T>) {
    const editing = rowBeingEdited.value?.id == row.id
    if (editing) {
      if (!onSaveValidation) {
        exitEditMode()
        return
      }
      if (await onSaveValidation(row)) {
        exitEditMode()
      }
    } else {
      enterEditMode(row)
    }
  }

  /**
   * Set the row to edit mode and cache its initial value
   */
  const enterEditMode = (row: ManagedRow<T>) => {
    rowBeingEdited.value = row
    let initialValue: T
    try {
      initialValue = structuredClone(toRaw(row))
    } catch {
      initialValue = JSON.parse(JSON.stringify(toRaw(row)))
    }
    row.initialValue = initialValue
  }

  /**
   * Reset the row to its initial state
   */
  const cancelEditMode = () => {
    if (!rowBeingEdited.value?.initialValue) return
    Object.assign(rowBeingEdited.value, rowBeingEdited.value.initialValue)
    delete rowBeingEdited.value.initialValue
    rowBeingEdited.value = undefined
  }

  /**
   * Update the part on the BE and sync managed state on success
   */
  const exitEditMode = async () => {
    const row = rowBeingEdited.value

    if (!row || !row.id || !row.initialValue) return

    const rowDiff = diff(row, row.initialValue)

    const meaningfulChanges = rowDiff.filter((change) => change.path[0] !== 'initialValue')

    // Row wasn't updated:
    if (!meaningfulChanges.length) {
      delete row.initialValue
      rowBeingEdited.value = undefined
      return
    }

    try {
      await onRowUpdate(<T>row)
      // Reset row managed state
      delete row.initialValue
      rowBeingEdited.value = undefined
    } catch (e: unknown) {
      cancelEditMode()
      const message = e instanceof Error ? e.message : 'Unknown error occurred'
      NotifyFail(message, {
        title: onUpdateFailedMessageTitle ?? 'Update failed',
      })
    }
  }

  onKeyStroke('Escape', cancelEditMode)
  onKeyStroke('Enter', exitEditMode)

  return { rowBeingEdited, onClickEdit }
}
